import type {MouseEventHandler, ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import {Panel, PanelContent, PanelFooter, PanelFooterSecondaryDefaultAction, PanelHeader} from '@Components/panel';
import {AiSubtitleChooseMedia} from '@Panels/ai-subtitle-panel/components/ai-subtitle-choose-media/ai-subtitle-choose-media';
import {AiSubtitleGenerating} from '@Panels/ai-subtitle-panel/components/ai-subtitle-generating/ai-subtitle-generating';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {getAudioItemsInOrder, getVideoItemsWithAudioInOrder, updateAiSubtitleState} from '@Components/poster-editor/poster-editor-reducer';
import {AiSubtitleItemResult} from '@Panels/ai-subtitle-panel/components/ai-subtitle-generating/ai-subtitle-generating.types';
import {noop} from '@Utils/general.util';
import type {AudioData, VideoData} from '@Libraries/add-media-library';
import {
  getAiTranscriptParamsForAudio,
  getAiTranscriptParamsForVideo,
  showAiSubtitlesUpsellDialog,
  getSelectableItemDataForAudioData,
  getSelectableItemDataForVideoData,
} from '@Libraries/ai-transcript.library';
import {
  addAiSubtitlesGenerationResults,
  addAiSubtitlesPanelItems,
  clearGeneratedResults,
  selectAiSubtitlePanelItems,
  setAiSubtitlesPanelItems,
  toggleAiSubtitlePanelItemSelection,
  unSelectAiSubtitlePanelItems,
} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import {useAiTranscriptGeneration} from '@Hooks/poster-editor/useAiTranscriptGeneration';
import {AiTranscriptAction} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {updatePanelSizeData} from '@Components/panel/panel-reducer';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {AiSubtitleGenerationLanguageSelector} from '@Panels/ai-subtitle-panel/components/ai-subtitle-generation-language-selector/ai-subtitle-generation-language-selector';
import {AiButton} from '@Components/ai-button';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import type {AiSubtitlesPanelProps, SelectableItemWithAudio} from './ai-subtitle-panel.types';
import {AI_SUBTITLE_PANEL_ID} from './ai-subtitle-panel.types';
import styles from './ai-subtitle-panel.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';

export default function AiSubtitlePanel({
  panelId = AI_SUBTITLE_PANEL_ID,
  doSetItemsOnMount = true,
  hideHeader = false,
  contentClasses = '',
  onDone = noop,
  showBackButton = true,
  isMobile = false,
  isOpenedInContextualMenu = false,
  ...props
}: AiSubtitlesPanelProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const onClose = props.onClose?.bind(null) ?? useDefaultPanelClose(panelId);
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');

  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());

  const scrollableContainerRef = useRef<HTMLDivElement | null>(null);

  const {topShadow, bottomShadow} = useTopBottomScrollShadow(scrollableContainerRef);
  const videos = useAppSelector(getVideoItemsWithAudioInOrder);
  const audios = useAppSelector(getAudioItemsInOrder);
  const {addAiTranscripts, getAiSubtitlesActionForItem} = useAiTranscriptGeneration();
  const aiSubtitlesState = useAppSelector((state) => {
    return state.aiSubtitle;
  });
  const aiSubtitleGenerationState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });

  const getInitialItems = (): SelectableItemWithAudio[] | undefined => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster) {
      return undefined;
    }

    const currentPage = poster.getCurrentPage();

    const videoItemsToAppearFirst: SelectableItemWithAudio[] = [];
    videos.forEach((videoItem, index) => {
      const loopCycles = currentPage.getDuration() / (videoItem.endTime - videoItem.startTime);
      const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForVideo(videoItem, loopCycles));

      if (action === AiTranscriptAction.GENERATED) {
        const loopCycles = currentPage.getDuration() / (videoItem.endTime - videoItem.startTime);
        videoItemsToAppearFirst.push(getSelectableItemDataForVideoData(videoItem, loopCycles, index + 1));
      }
    });

    const audioItemsToAppearFirst = audios
      .filter((audioItem) => {
        const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForAudio(audioItem));
        return action === AiTranscriptAction.GENERATED;
      })
      .map(getSelectableItemDataForAudioData);

    const remainingVideoItems: SelectableItemWithAudio[] = [];

    videos.forEach((videoItem, index) => {
      const loopCycles = currentPage.getDuration() / (videoItem.endTime - videoItem.startTime);
      const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForVideo(videoItem, loopCycles));
      if (action !== AiTranscriptAction.GENERATED) {
        remainingVideoItems.push(getSelectableItemDataForVideoData(videoItem, loopCycles, index + 1));
      }
    });

    const remainingAudioItems = audios
      .filter((audioItem) => {
        const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForAudio(audioItem));
        return action !== AiTranscriptAction.GENERATED;
      })
      .map(getSelectableItemDataForAudioData);

    return [...[...videoItemsToAppearFirst, ...audioItemsToAppearFirst], ...[...remainingVideoItems, ...remainingAudioItems]];
  };

  const getIntialItemsHashmap = (): Record<string, SelectableItemWithAudio> | undefined => {
    const initialItems = getInitialItems();
    if (!initialItems) {
      return undefined;
    }

    const hashmap: Record<string, SelectableItemWithAudio> = {};
    for (const item of initialItems) {
      hashmap[item.id] = item;
    }

    return hashmap;
  };

  const uploadedItemsRef = useRef<Record<string, SelectableItemWithAudio>>(getIntialItemsHashmap() ?? {});

  const onCloseTriggered = (): void => {
    onClose();
  };

  const selectedItems = Object.values(aiSubtitlesState.aiSubtitlesPanel.items).filter((item) => {
    return item.isSelected;
  });

  const getHeader = (): ReactElement<any> => {
    return (
      <div className={topShadow && aiSubtitleGenerationState !== AiSubtitleState.IDLE ? styles.headerBottomShadow : styles.transparentBottomBorder}>
        <PanelHeader
          backButtonIconSize={!isMobile ? IconSize.SIZE_ICON_16 : undefined}
          titleClasses={styles.aiTitleText}
          className={`_borderless spacing-m-b-4 ${!isMobile ? 'spacing-m-l-3 spacing-p-t-4' : 'text-center'}`}
          title={getPanelTitle()}
          showCloseButton={isMobile}
          onClose={onCloseTriggered}
          showBackButton={showBackButton}
          onBackButton={onBack}
        />
      </div>
    );
  };

  const getPanelTitle = (): string => {
    switch (aiSubtitleGenerationState) {
      case AiSubtitleState.GENERATING:
        return `${window.i18next.t('pmwjs_generating')}...`;
      case AiSubtitleState.IDLE:
        return window.i18next.t('pmwjs_ai_subtitles');
      case AiSubtitleState.READY_SUCCESS:
      case AiSubtitleState.READY_FAILED:
        return window.i18next.t('pmwjs_generation_summary');
      default:
        throw new Error(`Unhandled panel state for header: ${aiSubtitleGenerationState}`);
    }
  };

  const onSelectAll = (event?: React.MouseEvent): void => {
    event?.stopPropagation();
    event?.preventDefault();

    const idsToSelect = Object.values(aiSubtitlesState.aiSubtitlesPanel.items)
      .filter((item) => {
        if (!item.sourceProperties) return true;

        const action = getAiSubtitlesActionForItem(item.sourceProperties);
        return action !== AiTranscriptAction.GENERATED;
      })
      .map((item) => {
        return item.id;
      });

    dispatch(selectAiSubtitlePanelItems(idsToSelect));
  };

  const onDeselectAll = (event?: React.MouseEvent): void => {
    event?.stopPropagation();
    event?.preventDefault();

    const idsToUnSelect = Object.keys(aiSubtitlesState.aiSubtitlesPanel.items);
    dispatch(unSelectAiSubtitlePanelItems(idsToUnSelect));
  };

  const onItemClick = (itemId: string, event?: React.MouseEvent<HTMLElement>): void => {
    event?.stopPropagation();
    event?.stopPropagation();

    dispatch(toggleAiSubtitlePanelItemSelection(itemId));
  };

  const onSubtitleRetry = async (itemId: string): Promise<void> => {
    const item = Object.values(aiSubtitlesState.aiSubtitlesPanel.items).find((listItem) => {
      return listItem.id === itemId;
    });
    if (item) {
      dispatch(addAiSubtitlesGenerationResults({id: itemId, result: AiSubtitleItemResult.LOADING}));

      await addAiTranscripts([item.sourceProperties], noop, false);
    }
  };

  const onUploadedItemAdded = async (items: (AudioData | VideoData)[]): Promise<void> => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }

    await currentPage.items.addItems.addNewItems(items, {selectOnAdd: false});

    const audioItems = Object.values(currentPage.poster.audioClips.audioItemsHashMap).sort((a, b) => {
      if (a.onPosterStartTime !== b.onPosterStartTime) {
        return a.onPosterStartTime - b.onPosterStartTime;
      }
      return a.name.localeCompare(b.name);
    });

    const itemIdsInOrder = currentPage.items.getItemIdsInOrder();
    const videoItemIdsInOrder = itemIdsInOrder.filter((itemId) => {
      const item = currentPage.items.itemsHashMap[itemId];
      return item.isVideo();
    });
    const videoItemsInOrder = videoItemIdsInOrder.map((videoItemId) => {
      return currentPage.items.itemsHashMap[videoItemId] as VideoItem;
    });

    const newlyUploadedVideos: SelectableItemWithAudio[] = [];
    videoItemsInOrder.forEach((videoItem, index) => {
      if (!uploadedItemsRef.current[videoItem.uid]) {
        const loopCycles = currentPage.getDuration() / (videoItem.endTime - videoItem.startTime);
        const selectableItemData = getSelectableItemDataForVideoData(videoItem.toObject(), loopCycles, index + 1);
        newlyUploadedVideos.push({...selectableItemData, isSelected: true});
        uploadedItemsRef.current[videoItem.uid] = {...selectableItemData, isSelected: true};
      }
    });

    const newlyUploadedAudios: SelectableItemWithAudio[] = [];
    audioItems.forEach((audioItem) => {
      if (!uploadedItemsRef.current[audioItem.uid]) {
        const selectableItemData = getSelectableItemDataForAudioData(audioItem.toObject());
        newlyUploadedAudios.push({...selectableItemData, isSelected: true});
        uploadedItemsRef.current[audioItem.uid] = {...selectableItemData, isSelected: true};
      }
    });

    const newlyUploadedItems = [...newlyUploadedVideos, ...newlyUploadedAudios];

    dispatch(addAiSubtitlesPanelItems([...newlyUploadedItems]));
  };

  const getContent = (): ReactElement<any> => {
    switch (aiSubtitleGenerationState) {
      case AiSubtitleState.IDLE:
        return (
          <AiSubtitleChooseMedia
            scrollableContainerRef={scrollableContainerRef}
            areUploadsBeingProcessed={aiSubtitlesState.aiSubtitlesPanel.areUploadsBeingProcessed}
            items={Object.values(aiSubtitlesState.aiSubtitlesPanel.items)}
            onDeselectAll={onDeselectAll}
            onSelectAll={onSelectAll}
            onItemClick={onItemClick}
            onItemAddedViaUploads={onUploadedItemAdded}
            forceHorizontalScrollOnOverflow={isMobile}
          />
        );
      case AiSubtitleState.READY_SUCCESS:
      case AiSubtitleState.READY_FAILED:
      case AiSubtitleState.GENERATING:
        return (
          <AiSubtitleGenerating
            isMobile={isMobile}
            scrollableContainerRef={scrollableContainerRef}
            isOpenedInContextMenu={isOpenedInContextualMenu}
            onRetry={onSubtitleRetry}
            items={selectedItems}
            results={aiSubtitlesState.aiSubtitlesPanel.results}
          />
        );
      default:
        throw new Error(`Unhandled panel state for header: ${aiSubtitleGenerationState}`);
    }
  };

  const onDoneClicked: MouseEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();

    dispatch(updateAiSubtitleState(AiSubtitleState.IDLE));

    onDone();
  };

  const getGenerateButtonText = (): string => {
    if (!isPremium.current) {
      return window.i18next.t('pmwjs_upgrade_to_generate');
    }

    return `${window.i18next.t('pmwjs_generate')}${selectedItems.length > 0 ? ` (${selectedItems.length})` : ''}`;
  };

  const getPrimaryAction = (): ReactElement<any>[] => {
    switch (aiSubtitleGenerationState) {
      case AiSubtitleState.GENERATING:
      case AiSubtitleState.READY_SUCCESS:
      case AiSubtitleState.READY_FAILED:
        return [
          <PanelFooterSecondaryDefaultAction
            disabled={aiSubtitleGenerationState === AiSubtitleState.GENERATING}
            text={window.i18next.t('pmwjs_done')}
            key="ai-subtitles-done"
            onClick={onDoneClicked}
          />,
        ];
      case AiSubtitleState.IDLE:
        return [
          <div className="_full-width">
            <AiSubtitleGenerationLanguageSelector isMobile={isMobile} isDisabled={!selectedItems.length} />
            <div className={`spacing-m-t-4`}>
              <AiButton
                variant={!isPremium.current ? AIButtonVariant.PREMIUM : undefined}
                isFullWidth
                icon="icon-ai-subtitles"
                isLoadingButtonOverlayShown
                key="ai-subtitle-cta"
                customClasses="_full-width _full-height"
                disabled={isCTADisabled()}
                text={getGenerateButtonText()}
                onClick={onGenerateSubtitles}
              />
            </div>
          </div>,
        ];
      default:
        throw new Error(`Unhandled panel state for ai subtitle panel for footer cta: ${aiSubtitleGenerationState}`);
    }
  };

  const isCTADisabled = (): boolean => {
    if (!isPremium.current) {
      return false;
    }

    switch (aiSubtitleGenerationState) {
      case AiSubtitleState.GENERATING:
      case AiSubtitleState.READY_SUCCESS:
      case AiSubtitleState.READY_FAILED:
        return false;
      case AiSubtitleState.IDLE:
        return selectedItems.length <= 0;
      default:
        return true;
    }
  };

  const onGenerateSubtitles = async (event?: React.MouseEvent): Promise<void> => {
    if (!isPremium.current) {
      void showAiSubtitlesUpsellDialog();
      return;
    }

    event?.stopPropagation();
    event?.preventDefault();

    dispatch(clearGeneratedResults());
    scrollableContainerRef.current = null;
    dispatch(updateAiSubtitleState(AiSubtitleState.GENERATING));

    await addAiTranscripts(
      selectedItems.map((selectedItem) => {
        return selectedItem.sourceProperties;
      })
    );
  };

  const showTopShadowOnFooter = (): boolean => {
    if (!bottomShadow) {
      return false;
    }

    if (aiSubtitleGenerationState === AiSubtitleState.IDLE) {
      if (isMobile) {
        return false;
      }

      return Object.values(aiSubtitlesState.aiSubtitlesPanel.items).length > 7;
    }

    if (isMobile) {
      return aiSubtitlesState.generationsBatch.totalItemsToGenerateSubtitlesFor > 7;
    }

    return aiSubtitlesState.generationsBatch.totalItemsToGenerateSubtitlesFor > 5;
  };

  const getFooter = (): ReactElement<any> => {
    return (
      <div>
        <div className={showTopShadowOnFooter() ? styles.oneSidedBoxShadow : styles.transparentTopBorder} />
        <PanelFooter className={`spacing-p-1 ${styles.footer}`} primaryActions={getPrimaryAction()} fullWidthPrimaryActions />
      </div>
    );
  };

  const onBack: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    switch (aiSubtitleGenerationState) {
      case AiSubtitleState.READY_SUCCESS:
      case AiSubtitleState.READY_FAILED:
        if (props.onBack) {
          props.onBack(e);
        }
        dispatch(updateAiSubtitleState(AiSubtitleState.IDLE));
        break;
      case AiSubtitleState.IDLE:
      case AiSubtitleState.GENERATING:
        if (props.onBack) {
          props.onBack(e);
        }
        break;
      default:
        throw new Error(`Unhandled panel state for header: ${aiSubtitleGenerationState}`);
    }
  };

  useEffect(() => {
    if (aiSubtitleGenerationState === AiSubtitleState.IDLE) {
      if (doSetItemsOnMount) {
        dispatch(setAiSubtitlesPanelItems(getInitialItems() ?? []));
      }
    }
  }, []);

  useEffect(() => {
    if (isMobile && aiSubtitleGenerationState !== AiSubtitleState.IDLE) {
      const numberOfItemsProcessing = selectedItems.length;

      const numberOfRowstoShow = Math.min(6, numberOfItemsProcessing);
      const heightForEachRow = 62.5;

      dispatch(
        updatePanelSizeData({
          sizeDataId: panelId,
          sizeData: {
            height: `${270 + (numberOfRowstoShow - 1) * heightForEachRow}px`,
          },
        })
      );
    }
  }, [aiSubtitleGenerationState]);

  return (
    <Panel
      className={`_full-width ${props.className ?? ''}`}
      panelId={panelId}
      panelHeader={hideHeader ? null : getHeader()}
      panelContent={<PanelContent className={contentClasses}>{getContent()}</PanelContent>}
      panelFooter={getFooter()}
    />
  );
}
